import React from 'react';
import PropTypes from 'prop-types';
import DropdownList from '../common/DropdownList';
import InfoTooltip from '../common/InfoTooltip';
import EditLineItem from './EditLineItem';
import { formatCurrency } from '../../numberUtils';

function LineItems({
  invoiceId,
  category,
  startDate,
  endDate,
  lineItemsGroupName,
  descriptionLabel,
  hasDuration,
  rate,
  handleNewLineItem,
  handleUpdateLineItem,
  handleDeleteLineItem,
  handleUploadFile,
  csrfToken,
  lineItems,
  totalPay,
  totalTime,
  status,
}) {
  const numOfEdits = lineItems.filter((x) => x.feedback).length;
  return (
    <div className="line-items card p-5 border-0">
      {status === 'edits_requested' && numOfEdits > 0
        && (
        <div className="request-edits-line-items body-text-2 number-of-edits">
          <i className="fa fa-circle" />
          {`${numOfEdits} ${category}${hasDuration ? ' task' : ''}${numOfEdits > 1 ? 's' : ''} to edit`}
        </div>
        )}
      <div className={`card-body ${numOfEdits > 0 ? 'edits-requested' : ''}`}>
        <div className={`${category}-line-items`}>
          <DropdownList
            title={lineItemsGroupName}
            dropdownClass={hasDuration ? `${category}-hours` : `${category}s`}
            isSideBySide
            icon="caret"
          >
            <div className="header g-row--always">
              <div className="date">Date</div>
              <div className="optional-id">
                Optional ID
                <InfoTooltip
                  triggerClassName="optional-id-tooltip-trigger"
                  tooltipId="optional-id-tooltip"
                  tooltipPlacement="top"
                  tooltipText="This is an optional field you can use to capture project codes/ids, cost centers, or other helpful information."
                />
              </div>
              <div className="description">
                {descriptionLabel}
              </div>
              {hasDuration && <div className="hours-and-minutes">Hours</div>}
              <div className="total">Total</div>
              {!hasDuration && <div className="file" />}
              <div className="actions" />
            </div>
            <div>
              {lineItems.map((lineItem, index) => (
                <EditLineItem
                  {...lineItem}
                  index={index}
                  invoiceId={invoiceId}
                  key={lineItem.id}
                  category={category}
                  hasDuration={hasDuration}
                  rate={rate}
                  startDate={startDate}
                  endDate={endDate}
                  viewDocumentUrl={lineItem.view_file_url}
                  updateLineItem={handleUpdateLineItem}
                  deleteLineItem={handleDeleteLineItem}
                  uploadFile={handleUploadFile}
                  csrfToken={csrfToken}
                  status={status}
                />
              ))}
            </div>
            {(status === 'draft' || status === 'edits_requested')
              && (
              <a
                role="link"
                tabIndex={0}
                id="add-line-item"
                className="g-row--always"
                onClick={() => handleNewLineItem(category)}
              >
                <i className="fa fa-plus me-2" />
                <h5 className="uppercase-demibold">
                  Add
                  {category}
                  &nbsp;
                  {category !== 'expense' && 'Hours'}
                </h5>
              </a>
              )}
            <div className="totals g-row--always">
              <div className="spacer1" />
              <div className="line-item-category-total">
                Total
                {hasDuration ? category : `${category}s`}
              </div>
              {hasDuration
                && (
                <div className="line-item-time">
                  {totalTime.hours}
&nbsp;h.&nbsp;
                  {totalTime.minutes}
&nbsp;min.
                </div>
                )}
              <div className="line-item-total">{formatCurrency(totalPay)}</div>
              {!hasDuration && <div className="spacer2" />}
            </div>
          </DropdownList>
        </div>
      </div>
    </div>
  );
}

LineItems.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  handleNewLineItem: PropTypes.func.isRequired,
  handleUpdateLineItem: PropTypes.func.isRequired,
  handleDeleteLineItem: PropTypes.func.isRequired,
  handleUploadFile: PropTypes.func,
  totalTime: PropTypes.shape({
    hours: PropTypes.number,
    mintues: PropTypes.number,
  }),
  totalPay: PropTypes.number,
  lineItems: PropTypes.shape([]).isRequired,
  lineItemsGroupName: PropTypes.string.isRequired,
  descriptionLabel: PropTypes.string.isRequired,
  hasDuration: PropTypes.bool,
  rate: PropTypes.number,
  invoiceId: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  csrfToken: PropTypes.string,
  status: PropTypes.string.isRequired,
};

LineItems.defaultProps = {
  handleUploadFile: () => null,
  totalPay: 0,
  totalTime: { hours: 0, minutes: 0 },
  hasDuration: false,
  rate: null,
  csrfToken: '',
  startDate: '',
  endDate: '',
};

export default LineItems;
